// TripDetailView — Wrapper that wires TripCardFull into the trip detail route/view.
//
// Connects to existing data sources:
//   - trip data (passed as prop)
//   - flightStatuses (via useSimpleFlightTracking)
//   - departureCalculations (computed from trip flights + user profile)
//   - journeyDataMap (currently empty; populated when journey data service is available)
//
// Wires AirportIntelligenceService:
//   - When a navigation stage (arrival_navigation or navigate_to_gate) is active,
//     calls getNavigationGuidance and passes the result to the stage node's
//     navigationGuidance field via an enriched classifyStageNodes output.
//
// The 100ms render requirement (Req 11.1) is met by the existing architecture:
//   - classifyStageNodes is synchronous
//   - Airport intelligence is async with fallback (non-blocking)
//
// Validates: Requirements 11.1, 7.1, 8.1

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { TripCardFull } from './TripCardFull';
import { useSimpleFlightTracking } from '../../hooks/useSimpleFlightTracking';
import { calculateDepartureTime } from '../../services/departureCalculator';
import { getNavigationGuidance } from '../../services/airportIntelligenceService';
import { resolveTripStages, STAGES } from '../../services/journeyStageEngine';
import { colors, radii, fontWeights, spacing } from '../../styles/designTokens';

/**
 * @typedef {Object} TripDetailViewProps
 * @property {Object} trip - Full trip object
 * @property {Object} userProfile - User profile/attributes (Cognito attributes)
 * @property {() => void} onBack - Navigate back to trips list
 * @property {(tripId: string) => void} onTripDeleted - Callback when trip is deleted
 * @property {(updatedTrip: Object) => void} onTripUpdated - Callback when trip is updated
 */

/**
 * Checks if all flights in a trip are in the past (>24h ago).
 * @param {Array} flights
 * @returns {boolean}
 */
function areAllFlightsPast(flights) {
  if (!flights || flights.length === 0) return true;
  const now = new Date();
  return flights.every((flight) => {
    const arrivalTime = new Date(flight.arrivalTime || flight.departureTime);
    return (now - arrivalTime) / (1000 * 60 * 60) > 24;
  });
}


/**
 * Checks if a flight is trackable (within 48h of departure or in progress).
 * @param {Object} flight
 * @param {Date} now
 * @returns {boolean}
 */
function isFlightTrackable(flight, now) {
  const departureTime = new Date(flight.departureTime);
  const arrivalTime = new Date(flight.arrivalTime || flight.departureTime);
  const hoursUntilDeparture = (departureTime - now) / (1000 * 60 * 60);
  const hoursSinceArrival = (now - arrivalTime) / (1000 * 60 * 60);

  // Track if within 48h of departure OR currently in flight (not yet arrived)
  return hoursUntilDeparture <= 48 || (hoursSinceArrival < 0);
}

/**
 * Extracts a parsed user profile from Cognito userAttributes.
 * Mirrors the logic in TripCard.js buildProfileFromData.
 *
 * @param {Object|null} userAttributes - Cognito user attributes
 * @returns {Object|null}
 */
function extractUserProfile(userAttributes) {
  if (!userAttributes) return null;

  try {
    const profileData = userAttributes.address ? JSON.parse(userAttributes.address) : {};
    return {
      homeAddress: {
        city: profileData.city || '',
        state: profileData.state || '',
        zip: profileData.zip || '',
        fullAddress: profileData.fullAddress || profileData.address || '',
      },
      preferences: {
        tsaPrecheck: profileData.tsaPrecheck || false,
        clearMember: profileData.clearMember || false,
        mobilityNeeds: profileData.mobilityNeeds || false,
      },
      loungeMemberships: profileData.loungeMemberships || {},
      sub: userAttributes.sub,
    };
  } catch {
    return null;
  }
}

/**
 * Determines the active navigation stage and relevant flight for airport intelligence.
 *
 * @param {Object|null} stageResult - Result from resolveTripStages
 * @param {Object} trip - Trip data
 * @returns {{ stage: string|null, flight: Object|null, gate: string|null, airportCode: string|null }}
 */
function getNavigationContext(stageResult, trip) {
  if (!stageResult?.activeStage) return { stage: null, flight: null, gate: null, airportCode: null };

  const { stage } = stageResult.activeStage;

  if (stage === 'arrival_navigation' || stage === 'navigate_to_gate') {
    // Find the relevant flight for this stage
    const legIndex = stageResult.activeStage.legIndex ?? 0;
    const flight = trip.flights?.[legIndex] || trip.flights?.[0];

    if (!flight) return { stage, flight: null, gate: null, airportCode: null };

    let gate = null;
    let airportCode = null;

    if (stage === 'arrival_navigation') {
      // Use arrival airport and gate
      airportCode = flight.arrivalAirport || null;
      gate = flight.arrivalGate || null;
    } else {
      // navigate_to_gate — use departure airport and gate
      airportCode = flight.departureAirport || null;
      gate = flight.departureGate || flight.gate || null;
    }

    return { stage, flight, gate, airportCode };
  }

  return { stage: null, flight: null, gate: null, airportCode: null };
}

/**
 * TripDetailView is the wrapper component that connects TripCardFull
 * to all required data sources and services.
 *
 * It handles:
 * - Flight status tracking via useSimpleFlightTracking
 * - Departure time calculations
 * - Airport Intelligence Service integration for navigation guidance
 * - Back navigation to the trips list
 *
 * @param {TripDetailViewProps} props
 * @returns {React.ReactElement}
 */
export function TripDetailView({ trip, userProfile, onBack, onTripDeleted, onTripUpdated }) {
  const [departureCalculations, setDepartureCalculations] = useState({});
  const [navigationGuidance, setNavigationGuidance] = useState(null);

  const userId = trip?.userId;
  const tripId = trip?.tripId;

  // ── Flight tracking ─────────────────────────────────────
  const shouldAutoStart = useMemo(() => {
    if (!trip?.flights || trip.flights.length === 0) return false;
    if (areAllFlightsPast(trip.flights)) return false;
    const now = new Date();
    return trip.flights.some((flight) => isFlightTrackable(flight, now));
  }, [trip?.flights]);

  const { flightStatuses } = useSimpleFlightTracking(userId, tripId, {
    enableTracking: shouldAutoStart,
    refreshInterval: 600000, // 10 minutes
    autoStart: shouldAutoStart,
    maxAttempts: null,
  });

  // ── Departure calculations ──────────────────────────────
  const parsedProfile = useMemo(() => extractUserProfile(userProfile), [userProfile]);

  useEffect(() => {
    if (!trip?.flights?.length) return;
    if (areAllFlightsPast(trip.flights)) return;

    // Req 1.6: Skip calculation and mark unavailable when the profile lacks a non-empty home address
    if (!parsedProfile?.homeAddress?.city) {
      const unavailableCalcs = {};
      for (let i = 0; i < trip.flights.length; i++) {
        unavailableCalcs[i] = { unavailable: true };
      }
      setDepartureCalculations(unavailableCalcs);
      return;
    }

    let cancelled = false;

    const calculateDepartures = async () => {
      const calculations = {};

      for (let i = 0; i < trip.flights.length; i++) {
        const flight = trip.flights[i];
        try {
          const isDepartureFlight = i === 0;
          const isConnectingFlight =
            i > 0 && isSameDayConnection(flight, trip.flights[i - 1]);

          // Req 1.5: 10-second timeout for departure calculation
          const DEPARTURE_CALC_TIMEOUT = 10000;
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('__DEPARTURE_CALC_TIMEOUT__')), DEPARTURE_CALC_TIMEOUT)
          );

          const result = await Promise.race([
            calculateDepartureTime(
              flight,
              parsedProfile,
              isDepartureFlight,
              isConnectingFlight,
              i > 0 ? trip.flights[i - 1] : null
            ),
            timeoutPromise,
          ]);

          // Req 1.4: If calculator returns no result, mark unavailable
          if (!result) {
            calculations[i] = { unavailable: true };
          } else {
            calculations[i] = result;
          }
        } catch (error) {
          // Req 1.5: Timeout → mark unavailable
          if (error.message === '__DEPARTURE_CALC_TIMEOUT__') {
            calculations[i] = { unavailable: true };
          } else {
            // Req 1.4: Calculator error → propagate error
            calculations[i] = { error: error.message || 'Departure calculation failed' };
          }
        }
      }

      if (!cancelled) {
        setDepartureCalculations(calculations);
      }
    };

    calculateDepartures();
    return () => { cancelled = true; };
  }, [trip?.flights, parsedProfile]);

  // ── Airport Intelligence Service wiring ─────────────────
  // When a navigation stage is active, fetch navigation guidance asynchronously.
  // The UI renders immediately with whatever data is available (Req 11.1: 100ms).
  // Navigation guidance arrives async and enriches the stage node.
  useEffect(() => {
    if (!trip?.flights?.length) return;

    let cancelled = false;

    const fetchGuidance = async () => {
      // Resolve current stage to determine if navigation guidance is needed
      const now = new Date();
      let stageResult = null;
      try {
        stageResult = resolveTripStages({
          trip,
          flightStatuses: flightStatuses || {},
          departureCalculations: departureCalculations || {},
          journeyDataMap: {},
          now,
        });
      } catch {
        return;
      }

      const navContext = getNavigationContext(stageResult, trip);
      if (!navContext.stage || !navContext.airportCode) {
        // No navigation stage active — clear any stale guidance
        if (!cancelled) setNavigationGuidance(null);
        return;
      }

      // Fetch navigation guidance (3s timeout built into the service)
      const guidance = await getNavigationGuidance(
        navContext.airportCode,
        navContext.gate || '',
        { timeout: 3000 }
      );

      if (!cancelled) {
        setNavigationGuidance(guidance);
      }
    };

    fetchGuidance();
    return () => { cancelled = true; };
  }, [trip, flightStatuses, departureCalculations]);

  // ── journeyDataMap — currently empty, populated when service is available ──
  const journeyDataMap = useMemo(() => ({}), []);

  // ── Back button handler ─────────────────────────────────
  const handleBack = useCallback(() => {
    if (onBack) onBack();
  }, [onBack]);

  // ── Render ──────────────────────────────────────────────
  return (
    <div data-testid="trip-detail-view" style={{ position: 'relative' }}>
      {/* Back navigation header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap,
          marginBottom: '16px',
          padding: '0 4px',
        }}
      >
        <button
          onClick={handleBack}
          data-testid="trip-detail-back-button"
          aria-label="Back to trips list"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: 'transparent',
            border: `1px solid ${colors.divider}`,
            borderRadius: radii.inner,
            color: colors.textPrimary,
            fontWeight: fontWeights.medium,
            fontSize: '14px',
            cursor: 'pointer',
            minWidth: '44px',
            minHeight: '44px',
            transition: 'background-color 0.15s',
          }}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
      </div>

      {/* TripCardFull — the full hierarchical journey view */}
      <TripCardFull
        trip={trip}
        userProfile={parsedProfile}
        flightStatuses={flightStatuses || {}}
        departureCalculations={departureCalculations}
        journeyDataMap={journeyDataMap}
        navigationGuidance={navigationGuidance}
        onBack={handleBack}
        onTripDeleted={onTripDeleted}
        onTripUpdated={onTripUpdated}
      />
    </div>
  );
}

/**
 * Checks if two consecutive flights are a same-day connection.
 * @param {Object} currentFlight
 * @param {Object} previousFlight
 * @returns {boolean}
 */
function isSameDayConnection(currentFlight, previousFlight) {
  if (!currentFlight || !previousFlight) return false;
  try {
    const currentDeparture = new Date(currentFlight.departureTime);
    const previousArrival = new Date(previousFlight.arrivalTime);
    const sameDay = currentDeparture.toDateString() === previousArrival.toDateString();
    const departureAfterArrival = currentDeparture > previousArrival;
    const isConnection = currentFlight.departureAirport === previousFlight.arrivalAirport;
    return sameDay && departureAfterArrival && isConnection;
  } catch {
    return false;
  }
}

export default TripDetailView;
